import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, Observable, Subject } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Recipient } from '@ms-organization-entities';
import { ListData, RecipientField } from '@pt/pt-models';
import { RecipientService } from '@pt/api-client';
import { takeUntil } from 'rxjs/operators';
import { ColumnWithLocalization } from 'it/primatoo/openapi/api/org/schemas/common/ColumnWithLocalization';
import { ColumnType } from 'it/primatoo/openapi/api/org/schemas/common/ColumnType';

@Component({
  selector: 'pt-select-recipient',
  template: `
    <div class='modal-header'>
      <h4 class='modal-title'>
        {{ title | async }}</h4>
    </div>

    <div class='modal-body'>
      <pt-select-recipient-table
        [data]='recipientsListData'
        [selectedRecipient]='selectedRecipient'
        (recipientSelected)='onSelectRecipient($event)'
      ></pt-select-recipient-table>
    </div>

    <div class='modal-footer'>
      <button type='button' class='btn btn-primary btn-sm'
              [disabled]='formGroup?.invalid'
              (click)='confirm(); $event.stopPropagation()'>
        {{ 'BUTTON.CONFIRM' | translate }}
      </button>
      <button ngbAutofocus type='button' class='btn btn-outline-secondary btn-sm'
              (click)='activeModal.close(false); $event.stopPropagation()'>
        {{ 'BUTTON.CANCEL' | translate }}
      </button>
    </div>
  `,
  styles: [``]
})
export class SelectRecipientDialogComponent implements OnInit, OnDestroy {
  @Input() title: Observable<string>;
  @Input() selectedRecipientId?: number;

  formGroup: FormGroup;

  public selectedRecipient: Recipient;
  public recipientsListData: ListData<Recipient>;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    public activeModal: NgbActiveModal,
    private recipientService: RecipientService
  ) {
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    forkJoin({
      data: this.recipientService.getRecipients(),
      cols: this.recipientService.findUserColumns()
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe((response: { data: Recipient[], cols: ColumnWithLocalization[] }) => {
      if (response?.data && response?.cols) {
        this.selectedRecipient = response.data?.find(r => r.id === this.selectedRecipientId);
        this.recipientsListData = {
          data: response.data,
          colEnabled: [
            {
              column: RecipientField.name,
              uiAvailable: true,
              uiUserShow: true,
              label: response.cols?.find(col => col.column === RecipientField.name)?.label,
              type: ColumnType.TEXT,
              operations: response.cols?.find(col => col.column === RecipientField.name)?.operations || []
            },
            {
              column: RecipientField.category,
              uiAvailable: true,
              uiUserShow: true,
              label: response.cols?.find(col => col.column === RecipientField.category)?.label,
              type: ColumnType.TEXT,
              operations: response.cols?.find(col => col.column === RecipientField.category)?.operations || []
            },
            {
              column: RecipientField.createdAt,
              uiAvailable: true,
              uiUserShow: true,
              label: response.cols?.find(col => col.column === RecipientField.createdAt)?.label,
              type: ColumnType.DATE,
              operations: response.cols?.find(col => col.column === RecipientField.createdAt)?.operations || []
            }
          ]
        };
      }
    });

    this.formGroup = new FormGroup({
      recipient: new FormControl(this.selectedRecipient ? this.selectedRecipient : null, [Validators.required])
    });
  }

  confirm() {
    const form = this.formGroup.getRawValue();
    if (form) {
      this.activeModal.close(form?.recipient);
    } else {
      this.activeModal.close(false);
    }
  }

  onSelectRecipient(selectedRecipient: Recipient) {
    this.formGroup.controls.recipient.setValue(selectedRecipient);
  }
}
